import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Zap, 
  Camera, 
  CheckCircle2, 
  Trophy, 
  Home as HomeIcon, 
  Package, 
  Star,
  ChevronDown
} from 'lucide-react';
import { mockData } from '../mock/data';

const Home = () => {
  const [activeTab, setActiveTab] = useState(null);

  const toggleFaq = (id) => {
    setActiveTab(activeTab === id ? null : id);
  };

  return (
    <div className="home-page">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-circle hero-circle-1"></div>
        <div className="hero-circle hero-circle-2"></div>
        <div className="hero-content">
          <h1>Nettoyage Airbnb Express à Meknès</h1>
          <h2>Garantissez votre note 4.9★</h2>
          <p className="subtitle">
            Intervention rapide, qualité constante, hôtes satisfaits.<br />
            Approuvé par des Superhosts.
          </p>
          <div className="trust-badges">
            <div className="trust-badge">
              <CheckCircle2 size={20} />
              <span>Checklist 27 points</span>
            </div>
            <div className="trust-badge">
              <Camera size={20} />
              <span>Photos après chaque nettoyage</span>
            </div>
            <div className="trust-badge">
              <Zap size={20} />
              <span>Réponse en 15 min</span>
            </div>
          </div>
          <div className="cta-buttons">
            <Link to="/contact" className="primary-btn">Réserver Maintenant</Link>
            <Link to="/tarifs" className="secondary-btn">Voir nos tarifs</Link>
          </div>
          <p className="small-text">
            <span>📍</span> Disponible à Meknès | Intervention same-day possible
          </p>
        </div>
      </section>

      {/* Problem Statement Section */}
      <section className="problem-section">
        <div className="section-container">
          <h2>Les Problèmes des Hôtes Airbnb à Meknès</h2>
          <div className="problem-grid">
            <div className="problem-card">
              <div className="problem-icon">😰</div>
              <h3>Nettoyeur non fiable</h3>
              <p>"Des fois il vient, des fois non. Mon stress constant."</p>
            </div>
            <div className="problem-card">
              <div className="problem-icon">⭐</div>
              <h3>Reviews négatives</h3>
              <p>"Une seule review 'pas propre' = -20% de bookings"</p>
            </div>
            <div className="problem-card">
              <div className="problem-icon">⏰</div>
              <h3>Perte de temps</h3>
              <p>"Je passe 2h à vérifier après chaque nettoyage"</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section">
        <div className="section-container">
          <h2>Notre Solution: Zéro Stress, Qualité Garantie</h2>

          <div className="feature-row left">
            <div className="text-content">
              <div className="feature-icon">
                <Zap size={28} />
              </div>
              <h3>Intervention Rapide</h3>
              <p>Nettoyage complet en 2-3h. Same-day disponible.</p>
              <p>Vous avez un checkout à 11h, check-in à 15h? Pas de problème.</p>
            </div>
            <div className="image-content">
              <div className="placeholder-image">
                <Zap size={64} />
                <span>Nettoyage rapide</span>
              </div>
            </div>
          </div>

          <div className="feature-row right">
            <div className="image-content">
              <div className="placeholder-image">
                <Camera size={64} />
                <span>Photos WhatsApp</span>
              </div>
            </div>
            <div className="text-content">
              <div className="feature-icon">
                <Camera size={28} />
              </div>
              <h3>Photos Après Chaque Nettoyage</h3>
              <p>Vous recevez des photos avant/pendant/après sur WhatsApp.</p>
              <p>Vous savez que tout est nickel sans bouger.</p>
            </div>
          </div>

          <div className="feature-row left">
            <div className="text-content">
              <div className="feature-icon">
                <CheckCircle2 size={28} />
              </div>
              <h3>Checklist 27 Points</h3>
              <p>Rien n'est oublié. Jamais.</p>
              <p>Standards Airbnb respectés à chaque fois.</p>
            </div>
            <div className="image-content">
              <div className="placeholder-image">
                <CheckCircle2 size={64} />
                <span>Checklist complète</span>
              </div>
            </div>
          </div>

          <div className="feature-row right">
            <div className="image-content">
              <div className="placeholder-image">
                <Trophy size={64} />
                <span>Garantie qualité</span>
              </div>
            </div>
            <div className="text-content">
              <div className="feature-icon">
                <Trophy size={28} />
              </div>
              <h3>Garantie Note 4.9★</h3>
              <p>Si un guest se plaint de la propreté dans les 24h,</p>
              <p>on re-nettoie GRATUIT ou vous êtes remboursé 100%.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="testimonials-section">
        <div className="section-container">
          <h2>Ce Que Disent Nos Clients Superhosts</h2>
          <div className="testimonials-grid">
            {mockData.testimonials.map((testimonial) => (
              <div key={testimonial.id} className="testimonial-card">
                <div className="stars">
                  {[...Array(testimonial.stars)].map((_, i) => (
                    <Star key={i} size={20} fill="#FFC107" color="#FFC107" />
                  ))}
                </div>
                <p className="quote">"{testimonial.quote}"</p>
                <div className="client-info">
                  <img src={testimonial.avatar} alt={testimonial.name} className="avatar" />
                  <div>
                    <p className="name">{testimonial.name}</p>
                    <p className="badge">{testimonial.badge}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Quick Grid */}
      <section className="services-section">
        <div className="section-container">
          <h2>Nos Services Airbnb</h2>
          <div className="services-grid">
            {mockData.services.map((service) => {
              const IconComponent = service.icon === 'home' ? HomeIcon : service.icon === 'package' ? Package : Zap;
              return (
                <div key={service.id} className={`service-card ${service.featured ? 'featured' : ''}`}>
                  {service.featured && <div className="popular-badge">⭐ POPULAIRE</div>}
                  <div className="service-icon-wrapper">
                    <IconComponent size={48} />
                  </div>
                  <h3>{service.title}</h3>
                  <p>{service.description}</p>
                  <p className="price">{service.price}</p>
                  {service.featured ? (
                    <Link to="/contact" className="gradient-btn">S'abonner</Link>
                  ) : (
                    <Link to="/services" className="outline-btn">En savoir plus</Link>
                  )}
                </div>
              );
            })}
          </div>
          <div style={{ textAlign: 'center', marginTop: '40px' }}>
            <Link to="/tarifs" className="link-btn">Voir tous les tarifs →</Link>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="stats-section">
        <div className="stats-grid">
          {mockData.stats.map((stat, index) => (
            <div key={index} className="stat-item">
              <div className="stat-number">{stat.number}</div>
              <div className="stat-label">{stat.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Process Steps */}
      <section className="process-section">
        <div className="section-container">
          <h2>Comment Ça Marche?</h2>
          <div className="process-steps">
            <div className="step">
              <div className="step-number">1</div>
              <h3>Contactez-nous</h3>
              <p>WhatsApp ou formulaire. Réponse en 15 minutes.</p>
            </div>
            <div className="step-arrow">→</div>
            <div className="step">
              <div className="step-number">2</div>
              <h3>Réservez votre créneau</h3>
              <p>Choix de date/heure. Confirmation immédiate.</p>
            </div>
            <div className="step-arrow">→</div>
            <div className="step">
              <div className="step-number">3</div>
              <h3>On nettoie</h3>
              <p>Équipe arrive à l'heure. Photos en temps réel.</p>
            </div>
            <div className="step-arrow">→</div>
            <div className="step">
              <div className="step-number">4</div>
              <h3>Vous validez</h3>
              <p>Appartement prêt. Guest heureux = Bonne review.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="trust-section">
        <h2>Pensé pour protéger vos avis</h2>
        <div className="trust-stars">
          {[...Array(5)].map((_, i) => (
            <Star key={i} size={48} fill="#FFC107" color="#FFC107" />
          ))}
        </div>
        <div className="trust-badge-main">
          <CheckCircle2 size={20} />
          <span>Airbnb Friendly Service</span>
        </div>
      </section>

      {/* Final CTA */}
      <section className="final-cta">
        <h2>Prêt à Protéger Votre Note Airbnb?</h2>
        <p>Rejoignez 50+ hôtes Airbnb qui nous font confiance.</p>
        <div className="cta-buttons">
          <Link to="/contact" className="white-btn">Réserver Mon Nettoyage</Link>
          <a href={mockData.contact.whatsappLink} target="_blank" rel="noopener noreferrer" className="outline-white-btn">
            Parler sur WhatsApp
          </a>
        </div>
        <div className="contact-info">
          📞 {mockData.contact.phone} | 📍 {mockData.contact.location}
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq-section">
        <div className="section-container">
          <h2>Questions Fréquentes</h2>
          <div className="faq-container">
            {mockData.faq.map((item) => (
              <div key={item.id} className={`faq-item ${activeTab === item.id ? 'active' : ''}`}>
                <div className="faq-question" onClick={() => toggleFaq(item.id)}>
                  <span>{item.question}</span>
                  <ChevronDown 
                    size={24} 
                    className="faq-icon" 
                    style={{ 
                      transform: activeTab === item.id ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.3s'
                    }} 
                  />
                </div>
                <div className="faq-answer">
                  {item.answer}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;